import { toDisposable } from "../../../base/common/lifecycle";
import { safeStringify } from "../../../base/common/objects";
import { IIdentitiyService } from "../../auth/common/auth";
import { ILogService } from "../../log/common/log";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage";
import { BaseAnalyticsService } from "../common/analyticsService";
// import rybbit from "@rybbit/js";


export class RybbitAnalyticsService extends BaseAnalyticsService {

    private _identityService: IIdentitiyService | null = null;

    set identityService(value: IIdentitiyService | null) {
        if (this._identityService) throw new Error("RybbitAnalyticsService: identityService can only be set once");
        this._identityService = value;
        if (this._identityService) {
            this._identityService.onDidChange(() => {
                this.identify();
            });
            if (this._identityService.me) this.identify();
        }
    }

    constructor(
        private readonly config: {
            readonly siteId: string,
            trackErrors?: boolean,
        },
        @ILogService private readonly logger: ILogService,
        @IStorageService private readonly storageService: IStorageService,
    ) {
        super();

        // track page url changes
        const pageViewEvent = (state: PopStateEvent) => {
            this.onLogEvent("pageview", {
                reason: "popstate"
            });
        };
        window.addEventListener("popstate", pageViewEvent);
        this._register(toDisposable(() => {
            window.removeEventListener("popstate", pageViewEvent);
        }));

        if (config.trackErrors) {
            this.logger.info("RybbitTel: Error tracking enabled");
            window.addEventListener("error", this.onError);
            this._register(toDisposable(() => { window.removeEventListener("error", this.onError); }));
            window.addEventListener("unhandledrejection", this.onUnhandledRejection);
            this._register(toDisposable(() => { window.removeEventListener("unhandledrejection", this.onUnhandledRejection); }));
        }
    }

    protected onLogEvent(eventName: "pageview" | ({} & string), params?: { [key: string]: any; }, opts?: { type?: "error" }): void {

        params = params || {};
        if (params && !params["user_id"] && this.identityService?.me?.id) params["user_id"] = this.identityService.me.id;


        // https://rybbit.com/docs/api/sending-events#parameters
        let url: string | null = null;
        let body: Record<string, any> | null = null;
        const type = opts?.type || "custom_event";

        switch (eventName) {
            case "pageview": {
                url = "https://needle.tools/api/v1/rum/t";
                // add hostname to params for pageview so it's visible in Rybbit dashboard
                params["host"] = window.location.host;
                body = {
                    site_id: this.config.siteId,
                    type: "pageview",
                    pathname: window.location.pathname,
                    hostname: window.location.hostname,
                    page_title: document.title,
                    referrer: document.referrer,
                    user_agent: navigator.userAgent,
                    querystring: window.location.search,
                    language: navigator.language,
                    screenWidth: window.screen.width,
                    screenHeight: window.screen.height,
                    event_name: eventName,
                    properties: params ? safeStringify(params) : undefined,
                };
                break;
            }
            default: {
                url = "https://needle.tools/api/v1/rum/t"
                body = {
                    site_id: this.config.siteId,
                    type: type,
                    pathname: window.location.pathname,
                    event_name: eventName,
                    properties: params ? safeStringify(params) : undefined,
                }
                break;
            }
        }

        this.logger.debug("RybbitTel: Sending event", eventName);
        this.sendFetch(url, body);
    }


    private identify() {

        const user = this.identityService?.me;
        const userId = user?.id;
        if (!user || !userId?.trim()?.length) {
            this.storageService.store("rum-user-id", null, StorageScope.APPLICATION, StorageTarget.USER);
            this.logger.debug("RybbitTel: identify skipped, user not logged in");
            return;
        }

        try {
            this.storageService.store("rum-user-id", userId, StorageScope.APPLICATION, StorageTarget.USER);
        } catch (e) {
            this.logger.error("Could not persist user ID to localStorage");

        }

        const url = "https://needle.tools/api/v1/rum/identify"
        const body = {
            site_id: this.config.siteId,
            user_id: user.id,
            traits: {
                username: user.name || undefined,
                email: user.email || undefined,
                avatar_url: user.avatarUrl || undefined,
            },
            is_new_identify: true,
        };
        this.logger.debug("RybbitTel: Sending identify", body);
        this.sendFetch(url, body);
        this.logger.debug("RybbitTel: Identify sent", body);
    }

    private sendFetch(url: string, body: Record<string, any>) {

        fetch(url, {
            method: "POST",
            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json'
            },
            // Ensures request completes even if page unloads
            keepalive: true,
            // Allow CORS requests
            mode: 'cors',
            // Low priority to avoid blocking other requests
            priority: 'low',
        }).catch((error) => {
            this.logger.warn("RybbitTel: Failed to send event", error);
        });
    }



    // #region error tracking
    private onError = (event: ErrorEvent) => {
        this.logger.info("RybbitTel: Caught global error event");
        const errorData: ErrorData = {
            message: (event.message || "Unknown error").substring(0, 500),
            stack: (event.error?.stack || "").substring(0, 2000),
            filename: (event.filename)?.substring(0, 200),
            lineno: event.lineno,
            colno: event.colno,
            timestamp: Date.now(),
        };
        this.trackError(event.error?.name || "Error", errorData);
    }
    private onUnhandledRejection = (event: PromiseRejectionEvent) => {
        this.logger.info("RybbitTel: Caught unhandled rejection event");
        let errorData: ErrorData;
        if (event.reason instanceof Error) {
            errorData = {
                message: event.reason.message.substring(0, 500),
                stack: event.reason.stack?.substring(0, 2000),
                timestamp: Date.now(),
            };
        } else {
            const reasonString = typeof event.reason === "string" ? event.reason : safeStringify(event.reason);
            errorData = {
                message: reasonString.substring(0, 500),
                timestamp: Date.now(),
            };
        }
        this.trackError("UnhandledRejection", errorData);
    }

    private _errorTrackingCache = new Map<string, { time: number, count: number }>();

    private async trackError(errorName: string, error: ErrorData, context?: { [key: string]: any; }) {
        try {
            const errorKey = `${errorName}|${error.message}|${error.filename}|${error.lineno}|${error.colno}`;
            const now = Date.now();

            const cached = this._errorTrackingCache.get(errorKey);
            // don't log the same error too often
            const timeThreshold = 1 * 60 * 1000;
            if (cached && now - cached.time < timeThreshold) {
                this.logger.debug(`RybbitTel: Skipping error log for ${errorName}, already logged recently`);
                cached.count++;
                return;
            }
            this._errorTrackingCache.set(errorKey, { time: now, count: (cached?.count || 0) + 1 });

            // https://github.com/rybbit-io/rybbit-js/blob/c7cf42abb7743b2770852152556fc0486d261096/src/errorTracking.ts#L99
            const properties: Record<string, any> = {
                error_name: errorName,
                message: typeof error === "string" ? error : error.message,
                ...context
            };

            if (cached && cached?.count > 1) {
                properties.error_name += ` x${cached.count}`;
            }

            if (error.stack) properties.stack = error.stack;
            if (error.filename) properties.filename = error.filename;
            if (error.lineno) properties.line_number = error.lineno;
            if (error.colno) properties.column_number = error.colno;
            if (error.timestamp) properties.timestamp = error.timestamp;

            try {
                const errorLine = await tryGetErrorLine(error.stack).catch((e) => { this.logger.error(e) /* ignoring */ })
                if (errorLine) {
                    properties.stack += `\nCode: ${errorLine}`;
                }

            }
            catch (e) {
                this.logger.warn("Failed to get error line", e);
            }

            this.onLogEvent(errorName, properties, { type: "error" });
        }
        catch (e) {
            this.logger.warn("Failed to track error", e);
        }
    }
}

type ErrorData = {
    message?: string;
    stack?: string;
    filename?: string;
    lineno?: number;
    colno?: number;
    timestamp?: number;
}

const previousErrorLines: Map<string, Promise<string | null>> = new Map();
const sourcecodePromise: Map<string, Promise<string>> = new Map();

async function tryGetErrorLine(stack: string | undefined): Promise<string | null> {
    if (stack) {
        // find first occurance of at chrome-extension:// and capture filepath with :linenumber:columnnumber
        const line = /at (?<filepath>chrome-extension:\/\/[^\s]+):(?<linenumber>\d+):(?<columnnumber>\d+)/.exec(stack);
        if (line) {
            const filepath = line.groups?.filepath;
            const linenumber = line.groups?.linenumber;
            const columnnumber = line.groups?.columnnumber;
            const key = `${filepath}|${linenumber}|${columnnumber}`;

            let previousPromise = previousErrorLines.get(key);
            if (previousPromise) {
                return previousPromise;
            }

            const promise = (async () => {
                if (filepath && linenumber !== undefined) {
                    let promise = sourcecodePromise.get(filepath);
                    if (!promise) {
                        promise = fetch(filepath).then(res => res.text());
                        sourcecodePromise.set(filepath, promise);
                    }

                    return promise.then(sourcecode => {
                        const lines = sourcecode.split(/\r?\n/);
                        const lineNumber = parseInt(linenumber, 10);
                        if (lineNumber > 0 && lineNumber <= lines.length) {
                            const errorLine = lines[lineNumber - 1];
                            let result = errorLine;
                            if (columnnumber !== undefined) {
                                const col = parseInt(columnnumber, 10);
                                const startIndex = Math.max(0, col - 20);
                                const endIndex = Math.min(errorLine.length, col + 50);
                                result = errorLine.substring(startIndex, endIndex);
                            }
                            return result.trim();
                        }
                        return null;
                    });
                }
                return null;
            })();

            previousErrorLines.set(key, promise);
            return promise;
        }
    }

    return null;
}